import { Icon, Icons } from '@/components/icon';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
import { Combobox, ComboboxEmpty, ComboboxGroup, ComboboxItem } from '../ui/combobox';
import { P3 } from '../ui/text';

export interface ColumnOption {
  value: string;
  label: string;
  icon?: keyof typeof Icons;
  conditions?: import('./filter-chip').FilterCondition[];
  /** HTML input type for the filter value input (e.g. 'number'). Defaults to 'text'. */
  inputType?: string;
}

interface FilterButtonProps {
  columns: ColumnOption[];
  onColumnSelect?: (columnId: string) => void;
  className?: string;
}

export function FilterButton({ columns, onColumnSelect, className }: FilterButtonProps) {
  const { t } = useTranslation('desktop_ui');
  const searchAttributesText = t('components.contacts.filter.searchAttributes');
  const noColumnsFoundText = t('components.contacts.filter.noColumnsFound');

  const handleValueChange = (value: string) => {
    onColumnSelect?.(value);
  };

  const trigger = (
    <button
      type="button"
      className={cn(
        'group h-7 flex cursor-pointer items-center gap-1 rounded-full border border-dashed border-primary-20 bg-primary-3 px-2 py-1 outline-none hover:bg-primary-5',
        'aria-expanded:border-solid aria-expanded:border-primary-10 aria-expanded:bg-primary-5 aria-expanded:hover:bg-primary-7',
        className,
      )}
    >
      <Icon name={Icons.Sliders} className="text-m text-primary-70" />
      <P3 className="text-primary-70">{t('components.contacts.filter.button')}</P3>
      <Icon name={Icons.Plus} className="text-s text-primary-40" />
    </button>
  );

  return (
    <Combobox
      variant="filter"
      searchPlaceholder={searchAttributesText}
      options={columns}
      onValueChange={handleValueChange}
      trigger={trigger}
    >
      {({ filteredOptions }) => (
        <ComboboxGroup variant="filter">
          {filteredOptions.length === 0 ? (
            <ComboboxEmpty>{noColumnsFoundText}</ComboboxEmpty>
          ) : (
            filteredOptions.map((option) => {
              const columnOption = option as ColumnOption;
              return (
                <ComboboxItem key={option.value} option={option} variant="filter">
                  {(opt, _isSelected) => (
                    <div className="flex min-w-0 flex-1 items-center gap-1">
                      {columnOption.icon && (
                        <Icon name={Icons[columnOption.icon]} className="text-s text-primary-80" />
                      )}
                      <P3 className="truncate text-primary-100">{opt.label}</P3>
                    </div>
                  )}
                </ComboboxItem>
              );
            })
          )}
        </ComboboxGroup>
      )}
    </Combobox>
  );
}
